import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Field, FormError, SubmitButton } from "@/components/common";
import { LoginForm } from "@/pages/auth/Login";
import { ForgotForm } from "@/pages/auth/ForgotPassword";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

function Panel({ title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-md">
      <div className="gf-card p-8 gf-rise">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="portal-auth-title">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-slate-600">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export function PortalLogin() {
  const { garage, slug } = usePortal();
  const navigate = useNavigate();
  return (
    <Panel title={t("portal.loginTitle", { garage: garage.name })} subtitle={t("portal.loginSubtitle")}>
      <LoginForm testPrefix="portal-login" forgotHref={`/g/${slug}/forgot-password`} onSuccess={() => navigate(`/g/${slug}/account`)} />
      <p className="mt-6 text-center text-sm text-slate-600">
        {t("portal.noAccount")} <Link to={`/g/${slug}/signup`} className="font-semibold text-teal-700" data-testid="portal-to-signup-link">{t("portal.signup")}</Link>
      </p>
    </Panel>
  );
}

export function PortalForgot() {
  const { slug } = usePortal();
  return (
    <Panel title={t("auth.forgotTitle")} subtitle={t("auth.forgotSubtitle")}>
      <ForgotForm loginHref={`/g/${slug}/login`} />
    </Panel>
  );
}

export function PortalSignup() {
  const { garage, slug } = usePortal();
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (me) return <Navigate to={`/g/${slug}/account`} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setError(t("auth.passwordMismatch"));
    setBusy(true);
    setError("");
    try {
      const { confirm, ...payload } = f;
      const { data } = await api.post(`/portal/${slug}/signup`, { ...payload, ref: sessionStorage.getItem(`gf_ref_${slug}`) || null });
      sessionStorage.removeItem(`gf_ref_${slug}`);
      setMe(data);
      navigate(`/g/${slug}/account`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={t("portal.signupTitle", { garage: garage.name })} subtitle={t("portal.signupSubtitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="portal-signup-form">
        <FormError message={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("customers.firstName")} htmlFor="ps-first"><Input id="ps-first" required value={f.first_name} onChange={set("first_name")} data-testid="portal-signup-first-name-input" /></Field>
          <Field label={t("customers.lastName")} htmlFor="ps-last"><Input id="ps-last" value={f.last_name} onChange={set("last_name")} data-testid="portal-signup-last-name-input" /></Field>
        </div>
        <Field label={t("auth.email")} htmlFor="ps-email"><Input id="ps-email" type="email" required value={f.email} onChange={set("email")} data-testid="portal-signup-email-input" /></Field>
        <Field label={t("customers.phone")} htmlFor="ps-phone"><Input id="ps-phone" value={f.phone} onChange={set("phone")} data-testid="portal-signup-phone-input" /></Field>
        <Field label={t("auth.password")} htmlFor="ps-pw" hint={t("auth.passwordRule")}><Input id="ps-pw" type="password" minLength={8} required value={f.password} onChange={set("password")} data-testid="portal-signup-password-input" /></Field>
        <Field label={t("auth.confirmPassword")} htmlFor="ps-pw2"><Input id="ps-pw2" type="password" minLength={8} required value={f.confirm} onChange={set("confirm")} data-testid="portal-signup-confirm-input" /></Field>
        <SubmitButton busy={busy} className="h-11 w-full" style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="portal-signup-submit-button">{t("portal.createAccount")}</SubmitButton>
        <p className="text-center text-xs text-slate-500">{t("portal.privacyNote", { garage: garage.name })}</p>
      </form>
      <p className="mt-6 text-center text-sm text-slate-600">
        {t("portal.haveAccount")} <Link to={`/g/${slug}/login`} className="font-semibold text-teal-700" data-testid="portal-to-login-link">{t("portal.login")}</Link>
      </p>
    </Panel>
  );
}
